/* SNK artisan retail product page. Shopify remains authoritative for price, stock and checkout. */
(() => {
  'use strict';
  const instances = new WeakMap();
  const headerInstances = new WeakMap();
  const text = (root, selector, value) => { const el = root.querySelector(selector); if (el) el.textContent = value; };
  const cartBusy = () => document.body.classList.contains('show-cart-summary');

  class ArtisanProduct {
    constructor(root) {
      this.root = root;
      this.data = JSON.parse(root.querySelector('[data-product-data]').textContent);
      this.form = root.querySelector('.snk-purchase-form');
      this.select = root.querySelector('[data-variant-select]');
      this.qty = root.querySelector('[data-quantity]');
      this.addButton = root.querySelector('[data-add]');
      this.error = root.querySelector('[data-error]');
      this.dialog = root.querySelector('[data-photo-dialog]');
      this.sticky = root.querySelector('[data-sticky]');
      this.abort = new AbortController();
      this.busy = false;
      this.frame = null;
      this.money = new Intl.NumberFormat(this.data.locale || 'en-AU', { style: 'currency', currency: this.data.currency });
      this.on(this.form, 'submit', e => this.submit(e));
      this.on(root, 'change', e => {
        if (e.target.matches('[name="snk_variant_choice"]')) {
          this.select.value = e.target.value;
          // The real Shopify product-form select is also consumed by accelerated checkout.
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (e.target === this.select) this.changeVariant();
        else if (e.target.matches('[data-addon]')) this.update();
      });
      this.on(this.qty, 'blur', () => this.normaliseQuantity());
      this.on(this.qty, 'input', () => this.update());
      this.on(root.querySelector('[data-minus]'), 'click', () => this.stepQuantity(-1));
      this.on(root.querySelector('[data-plus]'), 'click', () => this.stepQuantity(1));
      this.on(root, 'click', e => {
        const thumb = e.target.closest('[data-media-select]');
        if (thumb) this.showMedia(thumb.dataset.mediaSelect);
        const zoom = e.target.closest('[data-zoom]');
        if (zoom) {
          const img = zoom.querySelector('img');
          const large = this.dialog.querySelector('[data-large-photo]');
          large.src = zoom.dataset.large; large.alt = img.alt || this.data.title;
          if (this.dialog.showModal) this.dialog.showModal();
          else window.open(zoom.dataset.large, '_blank', 'noopener');
          this.scheduleSticky();
        }
      });
      this.on(root.querySelector('[data-close-photo]'), 'click', () => this.dialog.close());
      this.on(this.dialog, 'close', () => this.scheduleSticky());
      this.on(root.querySelector('[data-sticky-add]'), 'click', () => {
        if (this.stickyMode === 'add') this.form.requestSubmit(this.addButton);
        else {
          root.querySelector('#choose-product').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
          const choice = root.querySelector('[name="snk_variant_choice"]:checked');
          const first = choice?.getClientRects().length ? choice : this.select.getClientRects().length ? this.select : this.qty;
          first.focus({ preventScroll: true });
        }
      });
      this.on(window, 'scroll', () => this.scheduleSticky(), { passive: true });
      this.on(window, 'resize', () => this.scheduleSticky(), { passive: true });
      this.on(window, 'pageshow', () => this.update());
      this.on(window, 'popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        if (this.data.variants.some(v => String(v.id) === id)) { this.select.value = id; this.changeVariant(false); }
      });
      this.bodyObserver = new MutationObserver(() => this.scheduleSticky());
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleSticky());
        this.resizeObserver.observe(this.form);
      }
      root.querySelector('[data-addons]')?.removeAttribute('hidden');
      root.classList.add('is-enhanced');
      // CSS keeps the canonical selector visible when longer kit choices need a compact layout.
      this.update();
      this.scheduleSticky();
    }
    on(el, event, callback, options = {}) { el?.addEventListener(event, callback, { ...options, signal: this.abort.signal }); }
    variant() { return this.data.variants.find(v => String(v.id) === this.select.value) || this.data.variants[0]; }
    limits() {
      const v = this.variant(), min = Math.max(1, Number(v.min) || 1), step = Math.max(1, Number(v.step) || 1);
      const max = v.max == null ? Number.MAX_SAFE_INTEGER : Math.max(0, Number(v.max));
      return { min, step, max };
    }
    quantity() {
      const { min, step, max } = this.limits();
      const raw = Math.trunc(Number(this.qty.value));
      const whole = Number.isFinite(raw) ? raw : min;
      const limit = max < min ? min : min + Math.floor((max - min) / step) * step;
      return Math.max(min, Math.min(limit, min + Math.floor(Math.max(0, whole - min) / step) * step));
    }
    extras() { return [...this.root.querySelectorAll('[data-addon]:checked:not(:disabled)')]; }
    selection() {
      const v = this.variant(), q = this.quantity(), extras = this.extras();
      return { v, q, extras, total: v.price * q + extras.reduce((n, e) => n + Number(e.dataset.price), 0), count: q + extras.length };
    }
    message(key, values = {}) { return Object.entries(values).reduce((value, [name, replacement]) => value.replaceAll('[' + name + ']', String(replacement)), this.data.messages[key]); }
    format(amount) { return this.money.format(amount / 100); }
    normaliseQuantity() {
      const previous = this.qty.value, next = String(this.quantity());
      this.qty.value = next;
      const note = this.root.querySelector('[data-quantity-notice]');
      note.hidden = previous === next;
      note.textContent = this.message('quantity_adjusted', { quantity: next });
      this.update();
    }
    stepQuantity(direction) {
      this.qty.value = this.quantity() + direction * this.limits().step;
      this.normaliseQuantity();
      this.qty.dispatchEvent(new Event('change', { bubbles: true }));
    }
    changeVariant(history = true) {
      const v = this.variant();
      this.root.querySelectorAll('[name="snk_variant_choice"]').forEach(r => { r.checked = r.value === String(v.id); });
      if (history) {
        const url = new URL(location.href); url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      }
      text(this.root, '[data-variant-caption]', v.title);
      if (v.mediaId) this.showMedia(String(v.mediaId));
      this.normaliseQuantity();
      this.root.dispatchEvent(new CustomEvent('snk:variant-change', { bubbles: true, detail: { variant: v } }));
    }
    update() {
      const { v, q, extras, total, count } = this.selection(), limits = this.limits();
      text(this.root, '[data-price]', v.priceText);
      const compare = this.root.querySelector('[data-compare]'); compare.textContent = v.compareText; compare.hidden = !(v.compare > v.price);
      text(this.root, '[data-unit]', v.unit);
      const saving = this.root.querySelector('[data-unit-saving]');
      if (saving) { saving.textContent = v.unitSaving || ''; saving.hidden = !v.unitSaving; }
      text(this.root, '[data-selection-label]', this.message(count === 1 ? 'selection_one' : 'selection_many', { count }));
      text(this.root, '[data-selection-total]', this.format(total));
      const summary = this.root.querySelector('[data-selection-summary]');
      summary.hidden = !extras.length;
      summary.textContent = `${q} × ${v.title === 'Default Title' ? this.data.title : v.title}` + extras.map(e => ` + 1 ${e.dataset.title}`).join('');
      this.addButton.disabled = this.busy || !v.available || limits.max < limits.min;
      text(this.root, '[data-add-label]', this.busy ? this.message('adding') : !v.available ? this.message('sold_out') : extras.length ? this.message('add_selection_cart') : this.message('add_cart'));
      text(this.root, '[data-add-price]', this.format(total) + ' →');
      this.qty.min = limits.min; this.qty.step = limits.step;
      if (v.max != null) this.qty.max = limits.max; else this.qty.removeAttribute('max');
      this.root.querySelector('[data-minus]').disabled = this.busy || q <= limits.min;
      this.root.querySelector('[data-plus]').disabled = this.busy || q + limits.step > limits.max || !v.available;
      this.root.querySelectorAll('[data-addon]').forEach(el => {
        el.closest('.addon').querySelector('.addon-state').textContent = el.disabled ? this.message('unavailable') : el.checked ? this.message('selected') : this.message('add');
      });
      const accelerated = this.root.querySelector('[data-accelerated]');
      if (accelerated) { accelerated.hidden = !v.available; accelerated.inert = this.busy; }
      text(this.root, '[data-sticky-selection]', (v.title === 'Default Title' ? '' : v.title + ' · ') + this.message('quantity', { quantity: q }) + (extras.length ? this.message(extras.length === 1 ? 'extra_one' : 'extra_many', { count: extras.length }) : ''));
      this.scheduleSticky();
    }
    showMedia(id) {
      if (!this.root.querySelector(`[data-media-panel="${id}"]`)) return;
      this.root.querySelectorAll('[data-media-panel]').forEach(panel => {
        panel.hidden = panel.dataset.mediaPanel !== id;
        if (panel.hidden) panel.querySelectorAll('video').forEach(video => video.pause());
        else panel.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      });
      this.root.querySelectorAll('[data-media-select]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mediaSelect === id)));
    }
    scheduleSticky() {
      if (this.frame !== null) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.updateSticky(); });
    }
    updateSticky() {
      const b = this.addButton.getBoundingClientRect(), ticket = this.root.querySelector('#choose-product').getBoundingClientRect();
      const product = this.root.querySelector('#product').getBoundingClientRect();
      this.stickyMode = b.bottom < 0 ? 'add' : 'choose';
      const guide = matchMedia('(max-width:760px)').matches && ticket.top > innerHeight * .55 && product.top < innerHeight;
      this.sticky.hidden = cartBusy() || this.dialog.open || (b.top >= 0 && b.bottom <= innerHeight) || !(this.stickyMode === 'add' || guide);
      const button = this.root.querySelector('[data-sticky-add]');
      button.disabled = this.busy || (this.stickyMode === 'add' && this.addButton.disabled);
      text(this.root, '[data-sticky-label]', this.stickyMode === 'add' ? (this.busy ? this.message('adding') : !this.variant().available ? this.message('sold_out') : this.message('add_selection')) : this.root.dataset.product === 'flour' ? this.message('choose_size') : this.message('choose_options'));
      text(this.root, '[data-sticky-total]', this.format(this.selection().total) + (this.stickyMode === 'add' ? ' →' : ' ↓'));
    }
    async refreshCart(open = false) {
      const response = await fetch(this.data.cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Cart refresh failed');
      const cart = await response.json();
      document.querySelectorAll('[data-snk-cart-count]').forEach(el => { el.textContent = cart.item_count; });
      if (typeof window.theme?.updateCartSummaries === 'function') window.theme.updateCartSummaries(open, true, false);
      document.dispatchEvent(new CustomEvent('snk:cart-updated', { detail: { cart }, bubbles: true }));
      return cart;
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      this.normaliseQuantity();
      if (!this.variant().available || !this.form.reportValidity()) return;
      this.error.hidden = true;
      this.root.querySelector('[data-cart-feedback]').hidden = true;
      this.root.querySelector('[data-cart-recovery]').hidden = true;
      const { v, q, extras } = this.selection();
      const data = new FormData(this.form), properties = {};
      for (const [key, value] of data) {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match && typeof value === 'string') properties[match[1]] = value;
      }
      // Upload/property customisers remain on the native form path; never silently discard a file.
      if ([...data.values()].some(value => value instanceof File && value.size > 0)) {
        this.showError(this.message('custom_product')); return;
      }
      const main = { id: v.id, quantity: q };
      if (Object.keys(properties).length) main.properties = properties;
      const plan = data.get('selling_plan'); if (plan) main.selling_plan = plan;
      const items = [main, ...extras.map(el => ({ id: Number(el.dataset.id), quantity: 1 }))];
      this.busy = true; this.form.setAttribute('aria-busy', 'true'); this.update();
      let accepted = false;
      try {
        const response = await fetch(this.data.addUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const result = await response.json();
        if (!response.ok || result.status >= 400) throw new Error(typeof result.description === 'string' ? result.description : result.message || this.message('add_failed'));
        accepted = true;
        const feedback = this.root.querySelector('[data-cart-feedback]');
        feedback.textContent = this.message('added'); feedback.hidden = false;
        this.root.querySelector('[data-cart-recovery]').hidden = false;
        if (this.data.cartType === 'go_to_cart' || this.data.cartType === 'add_and_redirect' || !window.theme?.updateCartSummaries) {
          window.location.assign(this.data.cartUrl); return;
        }
        await this.refreshCart(true);
      } catch (error) {
        // A timeout or stock error can leave a changed cart. Never automatically retry the POST.
        if (accepted) this.showError(this.message('refresh_failed'));
        else {
          this.showError(`${error.message || this.message('unconfirmed')} ${this.message('review_before_retry')}`);
          try { await this.refreshCart(false); } catch (_) { /* The visible cart link is the recovery path. */ }
        }
      } finally {
        this.busy = false; this.form.removeAttribute('aria-busy'); this.update();
      }
    }
    showError(message) {
      this.error.textContent = message; this.error.hidden = false;
      this.root.querySelector('[data-cart-recovery]').hidden = false;
      this.error.focus();
    }
    destroy() { this.abort.abort(); this.bodyObserver.disconnect(); this.resizeObserver?.disconnect(); if (this.frame !== null) cancelAnimationFrame(this.frame); }
  }
  function init(scope = document) {
    if (window.jQuery) {
      window.jQuery(window).off('cc-cart-updated.snkArtisan').on('cc-cart-updated.snkArtisan', (_event, payload) => {
        const items = payload?.cartItemData?.items;
        if (!Array.isArray(items)) return;
        const count = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        document.querySelectorAll('[data-snk-cart-count]').forEach(el => { el.textContent = count; });
      });
    }
    scope.querySelectorAll('[data-snk-product]').forEach(root => {
      if (instances.has(root)) return;
      try { instances.set(root, new ArtisanProduct(root)); } catch (error) { console.error('SNK product enhancement failed; native form remains available.', error); }
    });
    document.querySelectorAll('.snk-artisan-header').forEach(header => {
      if (headerInstances.has(header)) return;
      const abort = new AbortController(), menu = header.querySelector('#snk-mobile-menu'), toggle = header.querySelector('#snk-menu-toggle');
      if (!menu || !toggle) return;
      menu.hidden = true;
      toggle.addEventListener('click', () => { menu.hidden = !menu.hidden; toggle.setAttribute('aria-expanded', String(!menu.hidden)); }, { signal: abort.signal });
      header.addEventListener('keydown', e => { if (e.key === 'Escape' && !menu.hidden) { menu.hidden = true; toggle.setAttribute('aria-expanded', 'false'); toggle.focus(); } }, { signal: abort.signal });
      headerInstances.set(header, abort);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => { e.target.querySelectorAll('[data-snk-product]').forEach(root => { instances.get(root)?.destroy(); instances.delete(root); }); });
})();
